import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY_QUESTION = { question: "", options: ["", "", "", ""], correct_index: 0, explanation: "" };
const EMPTY = {
  title: "", description: "", category: "Agilité", duration: "3 min", published: true,
  questions: [{ ...EMPTY_QUESTION }],
  results: [
    { min_score: 0, title: "", message: "" },
    { min_score: 3, title: "", message: "" },
    { min_score: 5, title: "", message: "" },
  ],
};

export default function AdminQuizzes() {
  const [quizzes, setQuizzes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/quizzes").then((r) => setQuizzes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...editing, questions: editing.questions.filter((q) => q.question.trim()) };
      if (editing.quiz_id) await api.put(`/admin/quizzes/${editing.quiz_id}`, payload);
      else await api.post("/admin/quizzes", payload);
      toast.success("Quiz enregistré");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/quizzes/${id}`);
    toast.success("Quiz supprimé");
    load();
  };

  const setQ = (i, patch) => {
    const questions = editing.questions.map((q, j) => (j === i ? { ...q, ...patch } : q));
    setEditing({ ...editing, questions });
  };
  const setOpt = (i, oi, value) => {
    const options = editing.questions[i].options.map((o, j) => (j === oi ? value : o));
    setQ(i, { options });
  };
  const setRes = (i, patch) => {
    const results = editing.results.map((r, j) => (j === i ? { ...r, ...patch } : r));
    setEditing({ ...editing, results });
  };

  return (
    <div data-testid="admin-quizzes">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Quiz ({quizzes.length})</h2>
        <button data-testid="quiz-create-btn" onClick={() => setEditing(JSON.parse(JSON.stringify(EMPTY)))} className="btn-primary !py-2 !px-4 text-xs">
          <Plus size={14} /> Nouveau quiz
        </button>
      </div>
      <div className="space-y-3">
        {quizzes.map((q) => (
          <div key={q.quiz_id} className="border border-white/10 bg-[#111827] p-5 flex items-center gap-4" data-testid={`quiz-row-${q.quiz_id}`}>
            <div className="flex-1">
              <p className="text-sm text-slate-200 font-medium">{q.title}</p>
              <p className="text-xs text-slate-500 mt-1">
                {q.category} · {q.questions?.length || 0} questions {!q.published && <span className="text-amber-400">· Brouillon</span>}
              </p>
            </div>
            <button data-testid={`quiz-edit-${q.quiz_id}`} onClick={() => setEditing(q)} className="text-slate-500 hover:text-teal-300 transition-colors"><Pencil size={16} /></button>
            <button data-testid={`quiz-delete-${q.quiz_id}`} onClick={() => remove(q.quiz_id)} className="text-slate-600 hover:text-rose-400 transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#0f1524] border-white/10 text-slate-100 rounded-none" data-testid="quiz-editor-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{editing.quiz_id ? "Modifier le quiz" : "Nouveau quiz"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <input data-testid="quiz-input-title" className="input-dark" placeholder="Titre" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                <textarea data-testid="quiz-input-description" className="input-dark min-h-[60px]" placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-4">
                  <input className="input-dark" placeholder="Catégorie" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                  <input className="input-dark" placeholder="Durée (ex: 3 min)" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} />
                </div>

                <h3 className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-teal-300 pt-2">Questions</h3>
                {editing.questions.map((q, i) => (
                  <div key={i} className="border border-white/10 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="font-mono2 text-xs text-slate-500">Q{i + 1}</span>
                      <input data-testid={`quiz-question-input-${i}`} className="input-dark" placeholder="Intitulé de la question" value={q.question} onChange={(e) => setQ(i, { question: e.target.value })} />
                      {editing.questions.length > 1 && (
                        <button onClick={() => setEditing({ ...editing, questions: editing.questions.filter((_, j) => j !== i) })} className="text-slate-600 hover:text-rose-400"><X size={16} /></button>
                      )}
                    </div>
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input type="radio" name={`correct-${i}`} checked={q.correct_index === oi} onChange={() => setQ(i, { correct_index: oi })} title="Bonne réponse" />
                        <input className="input-dark !py-2 text-xs" placeholder={`Option ${String.fromCharCode(65 + oi)}`} value={opt} onChange={(e) => setOpt(i, oi, e.target.value)} />
                      </div>
                    ))}
                    <input className="input-dark !py-2 text-xs" placeholder="Explication (affichée après réponse)" value={q.explanation} onChange={(e) => setQ(i, { explanation: e.target.value })} />
                  </div>
                ))}
                <button data-testid="quiz-add-question-btn" onClick={() => setEditing({ ...editing, questions: [...editing.questions, JSON.parse(JSON.stringify(EMPTY_QUESTION))] })} className="text-sm text-teal-300 hover:text-teal-200">
                  + Ajouter une question
                </button>

                <h3 className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-teal-300 pt-2">Interprétation des scores</h3>
                {editing.results.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <input type="number" className="input-dark col-span-2 !py-2 text-xs" title="Score minimum" value={r.min_score} onChange={(e) => setRes(i, { min_score: parseInt(e.target.value || 0, 10) })} />
                    <input className="input-dark col-span-4 !py-2 text-xs" placeholder="Titre du profil" value={r.title} onChange={(e) => setRes(i, { title: e.target.value })} />
                    <input className="input-dark col-span-6 !py-2 text-xs" placeholder="Message / recommandation" value={r.message} onChange={(e) => setRes(i, { message: e.target.value })} />
                  </div>
                ))}

                <label className="flex items-center gap-2 text-sm text-slate-400">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="quiz-input-published" />
                  Publié
                </label>
                <button data-testid="quiz-save-btn" disabled={busy || !editing.title} onClick={save} className="btn-primary w-full disabled:opacity-50">
                  {busy ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
